import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { useAuth } from '../../core/authContext';
import { FEATURE_ASSIGNMENT_LIBRARY } from '../../core/features';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { TopBar } from '../IndividualPlan/TopBar';
import { PAGE_MAX_WIDTH } from '../../theme';
import { authSnapshot, type AuthSnapshot } from '../ProgressReports/students';
import { fetchImplementationReport, type Granularity, type ImplementationReportResponse, type ReportQuery, type UsageReport, type UsageStudent, type UsageTeacher } from './api';
import { localToday, monthLabel, periodOptions, schoolYearMonths, schoolYearStartOf, shortLabel } from './period';
import { csvText, delta, districtRows, downloadCsv, emptyTotals, loginsByMonth, monthSeries, previousSchoolTotals, schoolRows, teacherName, teacherTags, teachersAt, type TeacherRow } from './rows';
import { USAGE } from './tokens';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

export const IMPLEMENTATION_ROUTE = '/reports/implementation';
export const IMPLEMENTATION_PRINT_ROUTE = '/reports/implementation/print';

export type ReportView = 'district' | 'school' | 'teacher';

type Loaded = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; data: ImplementationReportResponse };

export function useReportQuery(): { granularity: Granularity; start?: string; search: string } {
  const [params] = useSearchParams();
  const raw = params.get('granularity');
  const granularity: Granularity = raw === 'month' || raw === 'year' ? raw : 'quarter';
  const start = params.get('start') ?? undefined;
  const search = `?granularity=${granularity}${start ? `&start=${start}` : ''}`;
  return { granularity, start, search };
}

export function useReport(source: (auth: AuthSnapshot, query: ReportQuery) => Promise<ImplementationReportResponse>, query: ReportQuery): Loaded {
  const { granularity, start, teacher, today } = query;
  const key = `${granularity}|${start ?? ''}|${teacher ?? ''}|${today ?? ''}`;
  const [settled, setSettled] = useState<{ key: string; loaded: Loaded } | null>(null);
  useEffect(() => {
    let active = true;
    authSnapshot()
      .then((auth) => source(auth, { granularity, start, teacher, today }))
      .then((data) => {
        if (active) setSettled({ key, loaded: { kind: 'ready', data } });
      })
      .catch(() => {
        if (active) setSettled({ key, loaded: { kind: 'error' } });
      });
    return () => {
      active = false;
    };
  }, [key, granularity, start, teacher, today, source]);
  return settled?.key === key ? settled.loaded : { kind: 'loading' };
}

const label = { fontFamily: USAGE.font, fontSize: USAGE.label.size, fontWeight: USAGE.label.weight, lineHeight: USAGE.label.lineHeight, letterSpacing: USAGE.label.letterSpacing, textTransform: 'uppercase' as const, color: USAGE.muted };
const body = { fontFamily: USAGE.font, fontSize: USAGE.body.size, fontWeight: USAGE.body.weight, lineHeight: USAGE.body.lineHeight, color: USAGE.ink };
const button = { display: 'inline-flex', alignItems: 'center', height: USAGE.control.height, px: '16px', borderRadius: `${USAGE.control.radius}px`, fontFamily: USAGE.font, fontSize: 16, fontWeight: 700, lineHeight: '24px', whiteSpace: 'nowrap', textDecoration: 'none', cursor: 'pointer', boxSizing: 'border-box' as const };
export const primaryButton = { ...button, bgcolor: USAGE.primary, color: USAGE.primaryContrast, border: 'none', '&:hover': { bgcolor: USAGE.primaryDark } };
export const outlinedButton = { ...button, bgcolor: USAGE.cardBg, color: USAGE.primary, border: `1px solid ${USAGE.primary}`, '&:hover': { bgcolor: USAGE.indigo50 } };

export function Card({ title, note, testId, children }: { title?: string; note?: string; testId?: string; children: ReactNode }) {
  return (
    <Box data-testid={testId ?? 'usage-card'} sx={{ bgcolor: USAGE.cardBg, border: `1px solid ${USAGE.divider}`, borderRadius: `${USAGE.card.radius}px`, p: `${USAGE.card.padding}px`, display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 0' }}>
      {title && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '16px', pb: '16px' }}>
          <Typography component="h2" sx={{ fontFamily: USAGE.font, fontSize: 20, fontWeight: 700, lineHeight: '28px', color: USAGE.ink }}>{title}</Typography>
          {note && <Typography sx={{ ...label, textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>{note}</Typography>}
        </Box>
      )}
      {children}
    </Box>
  );
}

export function Stats({ stats }: { stats: { label: string; value: string; sub: string }[] }) {
  return (
    <Box data-testid="usage-stats" sx={{ display: 'flex', bgcolor: USAGE.cardBg, border: `1px solid ${USAGE.divider}`, borderRadius: `${USAGE.card.radius}px` }}>
      {stats.map((stat, i) => (
        <Box key={stat.label} data-testid="usage-stat" sx={{ flex: '1 1 0', minWidth: 0, p: '24px', borderLeft: i === 0 ? 'none' : `1px solid ${USAGE.divider}`, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography data-testid="usage-stat-label" sx={label}>{stat.label}</Typography>
          <Typography data-testid="usage-stat-value" sx={{ fontFamily: USAGE.font, fontSize: USAGE.statValue.size, fontWeight: USAGE.statValue.weight, lineHeight: USAGE.statValue.lineHeight, letterSpacing: USAGE.statValue.letterSpacing, color: USAGE.ink }}>{stat.value}</Typography>
          <Typography data-testid="usage-stat-sub" sx={{ ...label, textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>{stat.sub}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function MonthChart({ months, values, inPeriod }: { months: string[]; values: number[]; inPeriod: (month: string) => boolean }) {
  const max = Math.max(1, ...values);
  return (
    <Box data-testid="usage-chart" sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: USAGE.chart.height, pt: '8px' }}>
      {months.map((month, i) => (
        <Box key={month} data-testid="usage-chart-column" data-in-period={String(inPeriod(month))} sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', height: '100%' }}>
          <Typography data-testid="usage-chart-value" sx={{ ...label, textTransform: 'none', letterSpacing: 0, color: USAGE.ink }}>{values[i]}</Typography>
          <Box sx={{ width: USAGE.chart.bar, maxWidth: '100%', borderRadius: '4px 4px 0 0', height: `${Math.max(2, Math.round(((values[i] ?? 0) / max) * 96))}px`, bgcolor: inPeriod(month) ? USAGE.primary : USAGE.indigo200 }} />
          <Typography data-testid="usage-chart-month" sx={{ ...label, textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>{monthLabel(month)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export type TableRow = { key: string; name: ReactNode; cells: (string | number)[]; total?: boolean };

export function UsageTable({ columns, rows, testId, compact = false }: { columns: string[]; rows: TableRow[]; testId?: string; compact?: boolean }) {
  const width = compact ? USAGE.columns.print : USAGE.columns.standard;
  const gap = compact ? '8px' : '16px';
  const cell = { flex: `0 0 ${width}px`, textAlign: 'right' as const, fontVariantNumeric: 'tabular-nums' };
  return (
    <Box data-testid={testId ?? 'usage-table'} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap, minHeight: 32, borderBottom: `2px solid ${USAGE.ink}` }}>
        <Typography sx={{ ...label, flex: '1 1 0' }}>{columns[0]}</Typography>
        {columns.slice(1).map((column) => <Typography key={column} sx={{ ...label, ...cell }}>{column}</Typography>)}
      </Box>
      {rows.map((row) => (
        <Box key={row.key} data-testid="usage-row" sx={{ display: 'flex', alignItems: 'center', gap, minHeight: compact ? 36 : 48, borderBottom: `1px solid ${USAGE.divider}`, ...body, fontSize: compact ? 14 : USAGE.body.size, fontWeight: row.total ? 700 : 400 }}>
          <Box data-testid="usage-row-name" sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center', gap: compact ? '4px' : '8px', flexWrap: compact ? 'wrap' : 'nowrap', py: compact ? '4px' : 0 }}>{row.name}</Box>
          {row.cells.map((value, i) => <Box key={i} data-testid="usage-cell" sx={cell}>{value}</Box>)}
        </Box>
      ))}
    </Box>
  );
}

export function Tag({ text, indigo, testId }: { text: string; indigo?: boolean; testId?: string }) {
  const colors = indigo ? USAGE.tagIndigo : USAGE.tagGrey;
  return <Box component="span" data-testid={testId} sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', borderRadius: '9999px', fontFamily: USAGE.font, fontSize: 12, fontWeight: 700, lineHeight: '20px', whiteSpace: 'nowrap', bgcolor: colors.bg, color: colors.fg }}>{text}</Box>;
}

const linkSx = { color: USAGE.primary, fontFamily: USAGE.font, fontSize: 16, fontWeight: 700, lineHeight: '24px', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };
const rowLinkSx = { color: USAGE.primary, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };

export function PeriodBar({ granularity, start, today, onChange, right }: { granularity: Granularity; start?: string; today: string; onChange: (next: { granularity: Granularity; start?: string }) => void; right: ReactNode }) {
  const options = periodOptions(granularity, today);
  const selected = start ?? options[0]?.start ?? '';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Box data-testid="usage-period-switch" role="group" aria-label="Period" sx={{ display: 'inline-flex', gap: '4px', p: '4px', height: USAGE.control.height, boxSizing: 'border-box', border: `1px solid ${USAGE.divider}`, borderRadius: `${USAGE.control.radius}px`, bgcolor: USAGE.cardBg }}>
          {(['month', 'quarter', 'year'] as Granularity[]).map((g) => (
            <Box key={g} component="button" type="button" aria-pressed={g === granularity} onClick={() => onChange({ granularity: g })} sx={{ px: '12px', border: 'none', borderRadius: '6px', fontFamily: USAGE.font, fontSize: 16, lineHeight: '30px', cursor: 'pointer', bgcolor: g === granularity ? USAGE.indigo50 : 'transparent', color: g === granularity ? USAGE.primary : USAGE.muted, fontWeight: g === granularity ? 700 : 500, '&:hover': { bgcolor: USAGE.hoverBg } }}>
              {g === 'month' ? 'Month' : g === 'quarter' ? 'Quarter' : 'Year'}
            </Box>
          ))}
        </Box>
        <Box component="select" aria-label="Choose a period" value={selected} onChange={(e) => onChange({ granularity, start: (e.target as HTMLSelectElement).value })} sx={{ height: USAGE.control.height, px: '12px', border: `1px solid ${USAGE.divider}`, borderRadius: `${USAGE.control.radius}px`, bgcolor: USAGE.cardBg, color: USAGE.ink, fontFamily: USAGE.font, fontSize: 16, fontWeight: 500, cursor: 'pointer' }}>
          {options.map((option) => <option key={option.start} value={option.start}>{option.label}</option>)}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>{right}</Box>
    </Box>
  );
}

export function Shell({ crumbs, children }: { crumbs: { label: string; to?: string }[]; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: USAGE.surface }}>
      <Box data-print="hide" sx={{ display: 'contents' }}><Sidebar sections={SIDEBAR_SECTIONS} activeId="reports" /></Box>
      <Box component="main" sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box data-print="hide"><TopBar crumbs={crumbs} /></Box>
        <Box data-testid="implementation-report-content" data-print="flat" sx={{ p: '24px', maxWidth: PAGE_MAX_WIDTH + 48, width: '100%', mx: 'auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

function statsFor(current: UsageReport['district'], previous: UsageReport['district'], prev: string) {
  return [
    { label: 'Teachers active', value: `${current.teachersActive} / ${current.teachersRostered}`, sub: `${prev}: ${previous.teachersActive} / ${previous.teachersRostered}` },
    { label: 'Students active', value: `${current.studentsActive} / ${current.studentsRostered}`, sub: `${prev}: ${previous.studentsActive} / ${previous.studentsRostered}` },
    { label: 'Toolbox views', value: String(current.toolboxViews), sub: delta(current.toolboxViews, previous.toolboxViews, prev) },
    { label: 'Academy started', value: `${current.academyStarted} / ${current.teachersRostered}`, sub: `${current.academyJoined} joined · ${current.academyFinished} finished a course` },
  ];
}

function dayLabel(iso: string, today: string): string {
  const base = `${monthLabel(iso.slice(0, 7))} ${Number(iso.slice(8, 10))}`;
  return iso.slice(0, 4) === today.slice(0, 4) ? base : `${base}, ${iso.slice(0, 4)}`;
}

export async function copyEmail(email: string): Promise<string> {
  try {
    if (!navigator.clipboard?.writeText) return 'Copying is not available in this browser';
    await navigator.clipboard.writeText(email);
    return `Copied ${email}`;
  } catch {
    return 'Could not copy the email address';
  }
}

export function fileLabel(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

// The server answers a sandbox district (or a super admin with no district) with the period and no usage,
// and a district whose Flashlight 360 resolver can't answer usage yet with only `available: false`.
export function Unavailable({ data }: { data: ImplementationReportResponse }) {
  if (data.period) {
    return <Card><Typography sx={{ ...body, fontWeight: 700 }}>{"Sandbox districts don't have usage data."}</Typography></Card>;
  }
  return (
    <Card>
      <Typography sx={{ ...body, fontWeight: 700 }}>Usage data is on its way.</Typography>
      <Typography sx={{ ...body, color: USAGE.muted }}>Sign-ins, Toolbox views and Academy progress will appear here once Flashlight360 starts sending them.</Typography>
    </Card>
  );
}

type Ready = { data: ImplementationReportResponse; current: UsageReport; previous: UsageReport; granularity: Granularity; today: string; prevShort: string; curShort: string; months: string[]; inPeriod: (m: string) => boolean };

function readyOf(data: ImplementationReportResponse, today: string): Ready | null {
  if (!data.available || !data.current || !data.previousReport || !data.period || !data.previous || !data.granularity) return null;
  const period = data.period;
  const prevShort = shortLabel(data.granularity, data.previous.start, data.previous.end);
  const curShort = shortLabel(data.granularity, period.start, period.fullEnd ?? period.end);
  const months = schoolYearMonths(schoolYearStartOf(period.start), period.end);
  const inPeriod = (m: string) => m >= period.start.slice(0, 7) && m <= period.end.slice(0, 7);
  return { data, current: data.current, previous: data.previousReport, granularity: data.granularity, today, prevShort, curShort, months, inPeriod };
}

export function ImplementationReportPage({
  view,
  source = fetchImplementationReport,
  onExport = downloadCsv,
  today = localToday(),
}: {
  view: ReportView;
  source?: (auth: AuthSnapshot, query: ReportQuery) => Promise<ImplementationReportResponse>;
  onExport?: (name: string, text: string) => void;
  today?: string;
}) {
  const { isDistrictAdmin, isSuperAdmin } = useAuth();
  const { schoolId, teacherId } = useParams();
  const navigate = useNavigate();
  const query = useReportQuery();
  const [copied, setCopied] = useState<string | null>(null);
  const loaded = useReport(source, { granularity: query.granularity, start: query.start, teacher: view === 'teacher' ? teacherId : undefined, today });
  const ready = useMemo(() => (loaded.kind === 'ready' ? readyOf(loaded.data, today) : null), [loaded, today]);
  if (!isDistrictAdmin && !isSuperAdmin) return <Navigate to="/reports" replace />;

  const school = ready?.current.schools.find((s) => s.schoolId === schoolId);
  const teacher = school?.teacherIds.includes(teacherId ?? '') ? ready?.current.teachers.find((t) => t.userId === teacherId) : undefined;
  const title = view === 'district' ? 'Implementation report' : view === 'school' ? school?.name ?? 'School' : teacher ? teacherName(teacher) : 'Teacher';
  const crumbs = [{ label: 'Reports', to: '/reports' }, ...(view === 'district' ? [{ label: 'Implementation report' }] : [{ label: 'Implementation report', to: IMPLEMENTATION_ROUTE + query.search }]),
    ...(view === 'school' ? [{ label: school?.name ?? 'School' }] : []),
    ...(view === 'teacher' ? [{ label: school?.name ?? 'School', to: `${IMPLEMENTATION_ROUTE}/school/${schoolId}${query.search}` }, { label: title }] : [])];
  const setQuery = (next: { granularity: Granularity; start?: string }) => {
    const params = new URLSearchParams({ granularity: next.granularity });
    if (next.start) params.set('start', next.start);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };

  const exportCsv = () => {
    if (!ready) return;
    const name = `implementation-report-${fileLabel(view === 'district' ? ready.curShort : `${title}-${ready.curShort}`)}-${ready.data.period!.fullEnd!.slice(0, 4)}.csv`;
    if (view === 'district') {
      const { schools, district } = districtRows(ready.current);
      onExport(name, csvText(['School', 'Teachers active', 'Teachers rostered', 'Toolbox views', 'Academy joined', 'Started', 'Finished'],
        [...schools, district].map((r) => [r.name, r.teachersActive, r.teachersRostered, r.toolboxViews, r.joined, r.started, r.finished])));
    } else if (view === 'school') {
      const { teachers, summary } = schoolTable(ready, schoolId ?? '');
      onExport(name, csvText(['Teacher', 'Students', 'Days logged in', 'Toolbox views', 'Students active', 'Courses started', 'Finished'],
        [...teachers.map((r) => [r.name, r.students, r.daysLoggedIn, r.toolboxViews, r.studentsActive, r.started, r.finished]), ['School', ...summary]]));
    } else if (teacher) {
      onExport(name, csvText(['Student', 'Active in period'], studentsAt(teacher, schoolId ?? '').map((s) => [`${s.firstName} ${s.lastName}`, s.active ? 'Yes' : 'No'])));
    }
  };

  const resolved = !!ready && (view === 'district' || (view === 'school' && !!school) || (view === 'teacher' && !!teacher));
  const right = (
    <>
      {resolved && <Box component="button" type="button" onClick={exportCsv} sx={outlinedButton}>Export</Box>}
      {resolved && view === 'district' && <Box component={Link} to={IMPLEMENTATION_PRINT_ROUTE + query.search} sx={primaryButton}>Print schools</Box>}
      {view === 'school' && school && <Box component={Link} to={`${IMPLEMENTATION_PRINT_ROUTE}${query.search}&school=${schoolId}`} sx={primaryButton}>Print school</Box>}
      {view === 'teacher' && teacher && (
        <Box component="button" type="button" sx={primaryButton} onClick={() => copyEmail(teacher.email).then(setCopied)}>Copy email address</Box>
      )}
    </>
  );

  return (
    <Shell crumbs={crumbs}>
      <Typography component="h1" sx={{ fontFamily: USAGE.font, fontSize: USAGE.title.size, fontWeight: USAGE.title.weight, lineHeight: USAGE.title.lineHeight, letterSpacing: USAGE.title.letterSpacing, color: USAGE.ink }}>{title}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <PeriodBar granularity={query.granularity} start={query.start} today={today} onChange={setQuery} right={right} />
        {copied && <Typography role="status" sx={{ ...body, color: USAGE.muted }}>{copied}</Typography>}
        {ready && view !== 'district' && !school && <Card><Typography sx={{ ...body, fontWeight: 700 }}>This school is not in the report.</Typography></Card>}
        {ready && view === 'teacher' && school && !teacher && <Card><Typography sx={{ ...body, fontWeight: 700 }}>This teacher is not at this school.</Typography></Card>}
        {loaded.kind === 'loading' && <Card><TableLoading tokens={USAGE} rows={4} /></Card>}
        {loaded.kind === 'error' && <Card><LoadFailure tokens={USAGE} /></Card>}
        {loaded.kind === 'ready' && !ready && <Unavailable data={loaded.data} />}
        {ready && (view === 'district' || (view === 'school' && school)) && <Stats stats={statsFor(view === 'district' ? ready.current.district : school?.totals ?? emptyTotals(), view === 'district' ? ready.previous.district : previousSchoolTotals(ready.previous, schoolId ?? ''), ready.prevShort)} />}
        {ready && view === 'teacher' && teacher && <TeacherStats ready={ready} teacher={teacher} />}
      </Box>
      {ready && view === 'district' && <DistrictBody ready={ready} search={query.search} />}
      {ready && view === 'school' && school && <SchoolBody ready={ready} schoolId={school.schoolId} search={query.search} />}
      {ready && view === 'teacher' && teacher && <TeacherBody ready={ready} teacher={teacher} schoolId={schoolId ?? ''} />}
    </Shell>
  );
}

function LoginChart({ ready, teacherIds, rostered }: { ready: Ready; teacherIds: string[]; rostered: number }) {
  return (
    <Card title="Teachers who logged in, by month" note={`of ${rostered} rostered`}>
      <MonthChart months={ready.months} values={loginsByMonth(ready.current, teacherIds, ready.months)} inPeriod={ready.inPeriod} />
    </Card>
  );
}

function Links({ month, schoolId, teacherId, first, search }: { month: string; schoolId?: string; teacherId?: string; first?: string; search?: string }) {
  const { hasFeature } = useAuth();
  const assignments = `/dashboard/assignments/${month}${schoolId ? `/school/${schoolId}` : ''}${teacherId ? `/teacher/${teacherId}` : ''}`;
  const benchmarks = `/benchmarks${schoolId ? `/schools/${schoolId}` : ''}${teacherId ? `/teachers/${teacherId}` : ''}`;
  return (
    <Box sx={{ display: 'flex', gap: '24px', pt: '16px' }}>
      <Box component={Link} to={assignments} sx={linkSx}>{first ? `${first}'s assignment activity →` : 'Assignment activity by teacher →'}</Box>
      {!teacherId && <Box component={Link} to={benchmarks} sx={linkSx}>Benchmark completion →</Box>}
      {!teacherId && hasFeature(FEATURE_ASSIGNMENT_LIBRARY) && <Box component={Link} to={`/reports/library${search ?? ''}`} sx={linkSx}>Assignment library →</Box>}
    </Box>
  );
}

function DistrictBody({ ready, search }: { ready: Ready; search: string }) {
  const { schools, district } = districtRows(ready.current);
  const rows: TableRow[] = [
    ...schools.map((r) => ({ key: r.schoolId, name: <Box component={Link} to={`${IMPLEMENTATION_ROUTE}/school/${r.schoolId}${search}`} sx={rowLinkSx}>{r.name}</Box>, cells: [`${r.teachersActive} / ${r.teachersRostered}`, r.toolboxViews, r.joined, r.started, r.finished] })),
    { key: 'district', name: district.name, cells: [`${district.teachersActive} / ${district.teachersRostered}`, district.toolboxViews, district.joined, district.started, district.finished], total: true },
  ];
  return (
    <>
      <LoginChart ready={ready} teacherIds={ready.current.teachers.map((t) => t.userId)} rostered={ready.current.district.teachersRostered} />
      <Card title={`By school · ${ready.curShort}`}>
        <UsageTable columns={['School', 'Teachers active', 'Toolbox', 'Academy joined', 'Started', 'Finished']} rows={rows} />
        <Links month={ready.data.period!.end.slice(0, 7)} search={search} />
      </Card>
    </>
  );
}

function teacherRow(r: TeacherRow, name: ReactNode): TableRow {
  return { key: r.userId, name: <>{name}{r.tags.map((tag) => <Tag key={tag} text={tag} indigo={tag === 'Not started yet'} />)}</>, cells: [r.students, r.daysLoggedIn, r.toolboxViews, r.studentsActive, r.started, r.finished] };
}

export const TEACHER_COLUMNS = ['Teacher', 'Students', 'Days logged in', 'Toolbox', 'Students active', 'Courses started', 'Finished'];

function schoolTable(ready: Ready, schoolId: string): { teachers: TeacherRow[]; summary: number[] } {
  const teachers = schoolRows(ready.current, schoolId);
  const school = ready.current.schools.find((s) => s.schoolId === schoolId)?.totals ?? emptyTotals();
  const total = teachers.reduce((acc, r) => ({ days: acc.days + r.daysLoggedIn, toolbox: acc.toolbox + r.toolboxViews, started: acc.started + r.started, finished: acc.finished + r.finished }), { days: 0, toolbox: 0, started: 0, finished: 0 });
  return { teachers, summary: [school.studentsRostered, total.days, total.toolbox, school.studentsActive, total.started, total.finished] };
}

function SchoolBody({ ready, schoolId, search }: { ready: Ready; schoolId: string; search: string }) {
  const { teachers, summary } = schoolTable(ready, schoolId);
  const rows: TableRow[] = [
    ...teachers.map((r) => teacherRow(r, <Box component={Link} to={`${IMPLEMENTATION_ROUTE}/school/${schoolId}/teacher/${r.userId}${search}`} sx={rowLinkSx}>{r.name}</Box>)),
    { key: 'school', name: 'School', cells: summary, total: true },
  ];
  return (
    <>
      <LoginChart ready={ready} teacherIds={teachersAt(ready.current, schoolId).map((t) => t.userId)} rostered={teachers.length} />
      <Card title={`Teachers · ${ready.curShort}`}>
        <UsageTable columns={TEACHER_COLUMNS} rows={rows} />
        <Links month={ready.data.period!.end.slice(0, 7)} schoolId={schoolId} search={search} />
      </Card>
    </>
  );
}

function TeacherStats({ ready, teacher }: { ready: Ready; teacher: UsageTeacher }) {
  const before = ready.previous.teachers.find((t) => t.userId === teacher.userId);
  const joined = teacher.academy.joinedAt;
  return (
    <Stats stats={[
      { label: 'Days logged in', value: String(teacher.daysLoggedIn), sub: `${ready.prevShort}: ${before?.daysLoggedIn ?? 0}` },
      { label: 'Students active', value: `${teacher.studentsActive} / ${teacher.studentsRostered}`, sub: `${ready.prevShort}: ${before?.studentsActive ?? 0} / ${before?.studentsRostered ?? 0}` },
      { label: 'Toolbox views', value: String(teacher.toolboxViews), sub: delta(teacher.toolboxViews, before?.toolboxViews ?? 0, ready.prevShort) },
      { label: 'Academy courses', value: joined ? `${teacher.academy.coursesStarted} started` : '—', sub: joined ? `${teacher.academy.coursesFinished} finished · joined ${dayLabel(joined, ready.today)}` : 'Not joined yet' },
    ]} />
  );
}

export function studentsAt(teacher: UsageTeacher, schoolId: string): UsageStudent[] {
  return (teacher.students ?? []).filter((s) => s.schoolId === schoolId).sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
}

function TeacherBody({ ready, teacher, schoolId }: { ready: Ready; teacher: UsageTeacher; schoolId: string }) {
  const joined = teacher.academy.joinedAt;
  const students = studentsAt(teacher, schoolId);
  return (
    <>
      <Box sx={{ display: 'flex', gap: '16px' }}>
        <Card title="Days logged in, by month" note={teacherName(teacher)}><MonthChart months={ready.months} values={monthSeries(teacher, ready.months, 'daysLoggedIn')} inPeriod={ready.inPeriod} /></Card>
        <Card title="Toolbox views, by month" note={teacherName(teacher)}><MonthChart months={ready.months} values={monthSeries(teacher, ready.months, 'toolboxViews')} inPeriod={ready.inPeriod} /></Card>
      </Box>
      <Card title={joined ? `Academy courses · joined ${dayLabel(joined, ready.today)}` : 'Academy courses'}>
        {joined && teacher.academy.courses.length > 0 ? (
          <UsageTable testId="usage-courses" columns={['Course', 'Steps viewed', 'Last viewed']} rows={teacher.academy.courses.map((c) => ({ key: c.courseId, name: c.title, cells: [`${c.stepsViewed} / ${c.steps}`, dayLabel(c.lastViewedAt, ready.today)] }))} />
        ) : (
          <Typography sx={{ ...body, color: USAGE.muted, py: '16px' }}>{joined ? 'No courses started yet.' : 'Not joined yet.'}</Typography>
        )}
      </Card>
      <Card title={`Students · ${ready.curShort}`}>
        <UsageTable testId="usage-students" columns={['Student', 'Active in period']} rows={students.map((s) => ({ key: s.userId, name: <Box component={Link} to={`/roster/${s.userId}`} sx={rowLinkSx}>{`${s.firstName} ${s.lastName}`}</Box>, cells: [s.active ? 'Yes' : 'No'] }))} />
        <Links month={ready.data.period!.end.slice(0, 7)} schoolId={schoolId} teacherId={teacher.userId} first={teacher.firstName} />
      </Card>
    </>
  );
}

export { readyOf, statsFor, teacherRow, teacherTags };
export type { Ready };
